import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleAlert, CircleCheckBig, CircleX, Clock, Download, Eye, Lock, Plus, RotateCcw, Search, Undo2 } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { DateInput, Select } from '../components/Controls';
import { download, fmtMoney, fmtNumber, t12, toCSV } from '../format';
import { Chip, Kpi, iso } from './attendance/shared';

// Time & Attendance > Overtime Authorization (Stephanet's OvertimeAuthorization): approval of weekday, Sunday/weekend
// and holiday overtime in the approval engine (OVERTIME). An approved request's amount goes on the payroll inputs
// register for its month, where the Payroll module takes it (then it shows as paid).

const STATUS_TONE = { Approved: 'green', Pending: 'yellow', Rejected: 'red', Returned: 'orange', Closed: 'gray' };
const TYPE_TONE = { Weekday: 'blue', Sunday: 'orange', Holiday: 'purple' };
const PAY_TONE = { paid: 'green', awaiting: 'yellow' };
const TYPES = [['weekday', 'Weekday'], ['weekend', 'Sunday / Weekend'], ['holiday', 'Holiday']];
// key: [label, icon, class, statuses it applies to, right]
const ACTIONS = {
  approve: ['Approve', CircleCheckBig, 'b-green', ['Pending'], 'approve'],
  reject: ['Reject', CircleX, 'b-red', ['Pending'], 'approve'],
  return: ['Return', Undo2, 'b-outline', ['Pending'], 'approve'],
  resubmit: ['Reopen', RotateCcw, 'b-blue', ['Rejected', 'Returned'], 'edit'],
  close: ['Close Record', Lock, 'b-outline', ['Approved'], 'approve'],
};
const NEEDS_REASON = { reject: 'Why is the overtime not authorised?', return: 'What needs checking before it can be approved?' };
const COLS = [['Record ID', r => r.reference], ['Employee', r => r.name], ['Employee ID', r => r.employee_no], ['Department', r => r.department || ''],
  ['OT Date', r => r.date], ['Hours', r => r.hours], ['Type', r => r.type], ['Amount', r => r.amount], ['Status', r => r.status],
  ['Approved By', r => r.approved_by || ''], ['Payroll', r => r.payroll || ''], ['Reason / Work Done', r => r.reason || ''], ['Decision Note', r => r.note || '']];
const rateBasis = salaryType => (salaryType === 'monthly' ? 'basic ÷ (working days × hours per day)' : 'daily rate ÷ hours per day');

export default function OvertimeAuthorization() {
  const { notify } = useHR();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [reason, setReason] = useState(null);

  const load = useCallback(() => hr.getOvertimeRegister()
    .then(x => { setD(x); setError(''); setSel(s => s.filter(id => x.rows.some(r => r.id === id))); return x; })
    .catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => {
    if (!d) return [];
    const s = q.trim().toLowerCase();
    return d.rows.filter(r => (!s || r.name.toLowerCase().includes(s) || String(r.employee_no || '').toLowerCase().includes(s) || String(r.reference).toLowerCase().includes(s))
      && (status === 'all' || r.status.toLowerCase() === status));
  }, [d, q, status]);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading overtime…" subtitle="Fetching overtime requests" />;
  const can = d.can;
  const act = (action, ids, comment) => {
    setBusy(true);
    return hr.overtimeAction({ action, ids, comment: comment || null })
      .then(r => { notify(r.message); setReason(null); setSel([]); return load(); })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false));
  };
  const run = (action, ids) => (NEEDS_REASON[action] ? setReason({ action, ids }) : act(action, ids));
  const chosen = d.rows.filter(r => sel.includes(r.id));
  const allOn = rows.length > 0 && rows.every(r => sel.includes(r.id));
  const k = d.kpis;

  return (
    <div className="mdr mds ota">
      <div className="mdr-top">
        <div>
          <h2>Overtime Authorization</h2>
          <p>Approval workflow for weekday, Sunday, and holiday overtime</p>
        </div>
        {can.create && <div className="mdr-top-actions"><button className="mdr-btn" onClick={() => setAdding(true)}><Plus size={14} /> New OT Request</button></div>}
      </div>

      <div className="md-kpis mds-kpis ts-kpis">
        <Kpi label="Total OT Records" value={k.total} Ico={Clock} tone="blue" note={`${fmtNumber(k.approved_hours)} approved hour(s)`} />
        <Kpi label="Approved" value={k.approved} Ico={CircleCheckBig} tone="green" note={`${fmtMoney(k.approved_amount)} · ${k.paid} paid, ${k.awaiting_payroll} awaiting payroll`} i={1} />
        <Kpi label="Pending" value={k.pending} Ico={CircleAlert} tone="amber" note={`${fmtMoney(k.pending_amount)} awaiting decision`} i={2} />
        <Kpi label="Rejected" value={k.rejected} Ico={CircleX} tone="red" note="Not authorised" i={3} />
      </div>

      <div className="ts-filters no-print">
        <div className="mdr-search ota-search"><Search size={14} /><input placeholder="Search employee name or ID..." value={q} onChange={e => setQ(e.target.value)} /></div>
        <Select value={status} onChange={e => setStatus(e.target.value)} title="Status" style={{ minWidth: 150 }}>
          <option value="all">All Status</option><option value="approved">Approved</option><option value="pending">Pending</option>
          <option value="returned">Returned</option><option value="rejected">Rejected</option><option value="closed">Closed</option>
        </Select>
        {can.export && <button className="mdr-btn" disabled={!rows.length} onClick={() => download('overtime-authorization.csv', toCSV(COLS.map(([label, get]) => ({ label, get })), rows))}><Download size={14} /> Export</button>}
      </div>

      {chosen.length > 0 && (
        <div className="mdr-bulk no-print">
          <b>{chosen.length} selected</b>
          {Object.entries(ACTIONS).map(([key, [label, Ico, cls, from, right]]) => {
            const ok = chosen.filter(r => from.includes(r.status));
            return can[right] && <button key={key} className={`mdr-bulk-btn ${cls}`} disabled={busy || !ok.length} onClick={() => run(key, ok.map(r => r.id))}><Ico size={13} /> {label}{ok.length ? ` (${ok.length})` : ''}</button>;
          })}
          <button className="mdr-bulk-btn b-outline" onClick={() => setSel([])}>Clear</button>
        </div>
      )}

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head"><b>Overtime Register</b><span className="mds-note-inline">{rows.length} record(s)</span></div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table ota-table">
            <thead>
              <tr>
                <th className="c no-print"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? [] : rows.map(r => r.id))} aria-label="Select all" /></th>
                <th className="c no-print">Action</th>
                {['Record ID', 'Employee', 'Department', 'OT Date', 'Hours', 'Type', 'Amount', 'Status', 'Approved By', 'Payroll'].map(h => <th key={h} className={['Hours', 'Amount'].includes(h) ? 'c' : ''}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  <td className="c no-print"><input type="checkbox" checked={sel.includes(r.id)} onChange={() => setSel(s => (s.includes(r.id) ? s.filter(x => x !== r.id) : [...s, r.id]))} aria-label={`Select ${r.reference}`} /></td>
                  <td className="c no-print nowrap">
                    <button className="mds-view" onClick={() => setViewing(r.id)}><Eye size={12} /> View</button>
                    {r.status === 'Pending' && can.approve && <>
                      <button className="mds-view ota-ok" disabled={busy} onClick={() => act('approve', [r.id])} title="Approve"><CircleCheckBig size={12} /></button>
                      <button className="mds-view ota-no" disabled={busy} onClick={() => run('reject', [r.id])} title="Reject"><CircleX size={12} /></button>
                    </>}
                  </td>
                  <td className="mono">{r.reference}</td>
                  <td><b>{r.name}</b><small className="ota-sub">{r.employee_no}</small></td>
                  <td className="small">{r.department || '—'}</td>
                  <td className="nowrap">{r.date}<small className="ota-sub">{r.day}</small></td>
                  <td className="c b">{fmtNumber(r.hours)}h</td>
                  <td><Chip tone={TYPE_TONE[r.type]}>{r.type}</Chip></td>
                  <td className="r b">{fmtMoney(r.amount)}</td>
                  <td><Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip></td>
                  <td className="small">{r.approved_by || '—'}</td>
                  <td>{r.payroll ? <Chip tone={PAY_TONE[r.payroll_state]}>{r.payroll}</Chip> : <span className="mds-dash">—</span>}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={12} className="mdr-empty">{d.rows.length ? 'No overtime records match.' : 'No overtime requests yet.'}</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr><td className="no-print" /><td className="no-print" /><td colSpan={4}><b>Total — {rows.length} record(s)</b></td>
                <td className="c">{fmtNumber(rows.reduce((a, r) => a + Number(r.hours || 0), 0))}h</td><td />
                <td className="r">{fmtMoney(rows.reduce((a, r) => a + r.amount, 0))}</td><td colSpan={3} /></tr></tfoot>
            )}
          </table>
        </div>
        <div className="mdr-foot att2-foot"><span>Amount = hours × hourly rate × OT multiplier (weekday, Sunday/weekend or holiday rate, or the employee's own overtime rate) · {d.requires_authorization
          ? 'approved overtime goes on the payroll inputs register for its month; overtime on attendance without an approval is not paid'
          : 'overtime is paid from attendance hours (authorisation not required)'}</span></div>
      </div>

      {adding && <NewRequest employees={d.employees} busy={busy} notify={notify} onClose={() => setAdding(false)}
        onSaved={() => { setAdding(false); load(); }} setBusy={setBusy} />}
      {viewing && <OTView id={viewing} busy={busy} notify={notify} onClose={() => setViewing(null)} onAction={(a, id) => run(a, [id])} />}
      {reason && <ReasonModal action={reason.action} count={reason.ids.length} busy={busy} onClose={() => setReason(null)}
        onSubmit={c => act(reason.action, reason.ids, c).then(() => setViewing(null))} />}
    </div>
  );
}

function NewRequest({ employees, busy, notify, onClose, onSaved, setBusy }) {
  const [f, setF] = useState({ employee_id: '', ot_date: iso(new Date()), hours: '', ot_type: '', reason: '' });
  const [est, setEst] = useState(null);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  useEffect(() => {
    if (!f.employee_id || !f.ot_date) { setEst(null); return undefined; }
    const t = setTimeout(() => hr.getOvertimeEstimate({ employee_id: f.employee_id, hours: Number(f.hours) || 0, ot_date: f.ot_date, ot_type: f.ot_type || undefined })
      .then(setEst).catch(() => setEst(null)), 250);
    return () => clearTimeout(t);
  }, [f.employee_id, f.ot_date, f.hours, f.ot_type]);
  const save = e => {
    e.preventDefault();
    if (!f.employee_id || !f.ot_date || !(Number(f.hours) > 0) || !f.reason.trim()) { notify('Employee, OT date, hours and reason are required', 'bad'); return; }
    setBusy(true);
    hr.createOvertime({ ...f, hours: Number(f.hours), ot_type: f.ot_type || null, reason: f.reason.trim() })
      .then(r => { notify(r.message); onSaved(); }).catch(er => notify(er.message, 'bad')).finally(() => setBusy(false));
  };
  const typeLabel = t => TYPES.find(x => x[0] === t)?.[1];
  return (
    <Modal className="wh-modal" title="New Overtime Request" onClose={onClose}>
      <form className="mdr-form wh-form" onSubmit={save} noValidate>
        <div className="lvm-grid">
          <label className="span2"><span>Employee <b className="wh-req">*</b></span>
            <Select value={f.employee_id} onChange={e => set('employee_id', e.target.value)}>
              <option value="">Select employee…</option>
              {employees.filter(x => x.eligible).map(x => <option key={x.id} value={x.id}>{x.name} ({x.employee_no})</option>)}
            </Select>
            {employees.some(x => !x.eligible) && <small className="muted">{employees.filter(x => !x.eligible).length} employee(s) are not overtime eligible and are not listed.</small>}
          </label>
          <label><span>OT Date <b className="wh-req">*</b></span><DateInput value={f.ot_date} onChange={e => set('ot_date', e.target.value)} /></label>
          <label><span>Hours <b className="wh-req">*</b></span><input type="number" min="0.5" max="24" step="0.5" value={f.hours} onChange={e => set('hours', e.target.value)} placeholder="e.g. 4" /></label>
          <label><span>Type</span>
            <Select value={f.ot_type} onChange={e => set('ot_type', e.target.value)}>
              <option value="">Auto from date{est ? ` (${typeLabel(est.suggested_type)})` : ''}</option>
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </label>
          <label className="span3"><span>Reason / Work Done <b className="wh-req">*</b></span><textarea rows={2} value={f.reason} onChange={e => set('reason', e.target.value)} placeholder="What was done during the overtime…" /></label>
        </div>
        <div className="ota-est">
          {est ? <>
            <div><span>Hourly rate</span><b>{fmtMoney(est.hourly)}</b><small>{rateBasis(est.salary_type)}</small></div>
            <div><span>Multiplier</span><b>× {est.multiplier}</b><small>{typeLabel(est.type)} · {est.rate_source}</small></div>
            <div className="tot"><span>Amount (est.)</span><b>{fmtMoney(est.amount)}</b><small>{fmtNumber(Number(f.hours) || 0)}h × {fmtMoney(est.hourly)} × {est.multiplier}</small></div>
            <div><span>Attendance that day</span><b>{est.attendance ? `${est.attendance.status}` : 'None'}</b>
              <small>{est.attendance ? `${fmtNumber(est.attendance.hours)}h worked · ${fmtNumber(est.attendance.ot || 0)}h OT recorded` : 'No attendance record for this date'}</small></div>
          </> : <p className="muted small">Choose an employee and date to see the estimated amount.</p>}
        </div>
        <div className="mdh-form-foot wh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="mds-run" disabled={busy}><Plus size={14} /> {busy ? 'Saving…' : 'Submit Request'}</button>
        </div>
      </form>
    </Modal>
  );
}

function OTView({ id, busy, notify, onClose, onAction }) {
  const [o, setO] = useState(null);
  useEffect(() => { hr.getOvertime(id).then(setO).catch(e => notify(e.message, 'bad')); }, [id, busy, notify]);
  if (!o) return <Modal title="Overtime" onClose={onClose}><Loader title="Loading…" subtitle="Fetching the record" /></Modal>;
  const a = o.attendance;
  const rows = [['Employee', `${o.name} (${o.employee_no})`], ['Department', o.department], ['OT Date', `${o.date} (${o.day})`], ['Hours', `${fmtNumber(o.hours)}h`],
    ['Type', o.type], ['Hourly Rate', `${fmtMoney(o.estimate.hourly)} (${rateBasis(o.salary_type)})`],
    ['Multiplier', `× ${o.estimate.multiplier} (${o.estimate.rate_source})`], ['Amount (est.)', fmtMoney(o.amount)], ['Reason / Work Done', o.reason],
    ['Status', o.status], ['Approved By', o.approved_by], ['Decision Date', o.decided_at && o.decided_at.replace('T', ' ').slice(0, 16)], ['Decision Note', o.note], ['Payroll', o.payroll],
    ['Attendance that day', a ? `${a.status} · ${fmtNumber(a.hours)}h worked · ${fmtNumber(a.ot || 0)}h OT recorded${a.time_in ? ` · ${t12(a.time_in)}–${t12(a.time_out) || '?'}` : ''}${a.site ? ` · ${a.site}` : ''}` : 'No attendance record for this date']];
  return (
    <Modal className="wh-modal" title={`${o.reference} — ${o.name}`} onClose={onClose}>
      <div className="ota-view-tags"><Chip tone={TYPE_TONE[o.type]}>{o.type}</Chip><Chip tone={STATUS_TONE[o.status]}>{o.status}</Chip></div>
      <table className="wh-view"><tbody>{rows.filter(([, v]) => v).map(([k2, v]) => <tr key={k2}><th>{k2}</th><td>{v}</td></tr>)}</tbody></table>
      {a && Number(a.ot || 0) < Number(o.hours) && <p className="ota-warn"><CircleAlert size={14} /> Attendance shows {fmtNumber(a.ot || 0)}h overtime that day — less than the {fmtNumber(o.hours)}h requested.</p>}
      {o.history.length > 0 && <><h4 className="wh-sub">History</h4><ul className="ts-hist">{o.history.map((h, i) => <li key={i}><b>{h.action}</b> · {h.user} · {(h.at || '').replace('T', ' ').slice(0, 16)}<br /><small>{h.summary}</small></li>)}</ul></>}
      <div className="mdh-form-foot">
        {Object.entries(ACTIONS).filter(([, [, , , from, right]]) => from.includes(o.status) && o.can[right]).map(([key, [label, Ico, cls]]) => (
          <button key={key} className={`mdr-bulk-btn ${cls}`} disabled={busy} onClick={() => onAction(key, o.id)}><Ico size={13} /> {label}</button>
        ))}
        <span className="wh-spacer" />
        <button className="mds-run" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function ReasonModal({ action, count, busy, onClose, onSubmit }) {
  const [c, setC] = useState('');
  const verb = action === 'reject' ? 'Reject' : 'Return';
  return (
    <Modal title={`${verb} ${count} overtime request(s)`} onClose={onClose}>
      <form className="mdr-form" onSubmit={e => { e.preventDefault(); if (c.trim()) onSubmit(c.trim()); }}>
        <label className="mdh-field">Reason<textarea rows={3} autoFocus value={c} onChange={e => setC(e.target.value)} placeholder={NEEDS_REASON[action]} /></label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="mds-run" disabled={busy || !c.trim()}>{verb}</button>
        </div>
      </form>
    </Modal>
  );
}
